import { SupportPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { userCallAvailabilitySchema } from "@redotech/redo-support-common/support/voice/voice-schema";
import {
  Call,
  CallState,
  UserCallAvailability,
} from "@redotech/redo-support-common/support/voice/voice-types";
import { VoiceProvider } from "@redotech/redo-support-settings-common/voice-settings";
import { sinkPromise } from "@redotech/util/promise";
import { createContext, useContext, useEffect, useState } from "react";

const TEN_SECONDS_IN_MS = 10_000;
const LOCAL_STORAGE_AVAILABILITY_KEY = "user-call-availability-status";

export const UserCallAvailabilityContext = createContext<
  | {
      availabilityStatus: UserCallAvailability;
      setAvailabilityStatus: (available: UserCallAvailability) => void;
      setCurrentCall: (call: Call | undefined) => void;
    }
  | undefined
>(undefined);

export function UserCallAvailabilityContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const client = useContext(RedoMerchantRpcClientContext);
  const hasSupportPermission = useHasPermission(
    SupportPermissions.VIEW_SUPPORT,
  );
  const getSavedStatus = (): UserCallAvailability => {
    try {
      const savedStatus = localStorage.getItem(LOCAL_STORAGE_AVAILABILITY_KEY);
      if (savedStatus) {
        const result = userCallAvailabilitySchema.safeParse(savedStatus);
        if (result.success) {
          return result.data;
        }
      }
    } catch (error) {
      console.error("Error reading saved call availability status", error);
    }
    return UserCallAvailability.UNAVAILABLE;
  };

  const [userDerivedStatus, setUserDerivedStatus] =
    useState<UserCallAvailability>(getSavedStatus());
  const [currentCall, setCurrentCall] = useState<Call | undefined>(undefined);
  const [currentTimeout, setCurrentTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  const voiceProvider =
    team.settings.support?.voice?.provider ?? VoiceProvider.TELNYX;
  const isTelnyxVoiceEnabled =
    voiceProvider === VoiceProvider.TELNYX &&
    !!team.settings.support?.voice?.enabled &&
    hasSupportPermission;

  const callDerivedStatus =
    currentCall && currentCall.state !== CallState.HANGUP
      ? UserCallAvailability.IN_CALL
      : undefined;

  const availabilityStatus = callDerivedStatus || userDerivedStatus;

  useEffect(() => {
    if (userDerivedStatus !== UserCallAvailability.IN_CALL) {
      try {
        localStorage.setItem(LOCAL_STORAGE_AVAILABILITY_KEY, userDerivedStatus);
      } catch (error) {
        console.error("Error saving call availability status", error);
      }
    }
  }, [userDerivedStatus]);

  const sendCurrentUserAvailability = async (
    availabilityStatus: UserCallAvailability,
  ) => {
    if (!isTelnyxVoiceEnabled) {
      return;
    }
    if (currentTimeout) {
      // If we switch between available and unavailable, we want to clear the previous timeout so it doesn't get reset
      clearTimeout(currentTimeout);
    }
    if (!client) {
      return;
    }
    try {
      await client.createCallAvailability({ availabilityStatus });
    } catch (error) {
      console.error("Failed to update call availability", error);
    } finally {
      // We don't want to continuously send the availability status for unavailable
      if (availabilityStatus !== UserCallAvailability.UNAVAILABLE) {
        const timeout = setTimeout(
          () => sendCurrentUserAvailability(availabilityStatus),
          TEN_SECONDS_IN_MS,
        );
        setCurrentTimeout(timeout);
      }
    }
  };

  useEffect(() => {
    sinkPromise(sendCurrentUserAvailability(availabilityStatus));
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityStatus]);

  useEffect(() => {
    const beforeUnloadHandler = async () => {
      await sendCurrentUserAvailability(UserCallAvailability.UNAVAILABLE);
    };

    window.addEventListener("beforeunload", beforeUnloadHandler);
    return () => {
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UserCallAvailabilityContext.Provider
      value={{
        availabilityStatus,
        setAvailabilityStatus: setUserDerivedStatus,
        setCurrentCall,
      }}
    >
      {children}
    </UserCallAvailabilityContext.Provider>
  );
}
